/**
 * A checkout the visitor started while signed out (e.g. from a /buy/:packageCode link in an
 * email or ad). Signing up or in can end on several pages (login, email or phone check), so
 * the intent is kept in sessionStorage and CheckoutIntentResumer takes them back to the
 * checkout once they are signed in, wherever that happens.
 */

const KEY = 'checkoutIntent';
/** An intent older than this is dropped: they have moved on, don't hijack a later visit. */
export const CHECKOUT_INTENT_TTL_MS = 2 * 60 * 60 * 1000;

export interface CheckoutIntent {
  packageCode: string;
  /** The /buy link's query string (e.g. ?discount=...), kept for the return trip. */
  search?: string;
  savedAt: number;
}

export function saveCheckoutIntent(packageCode: string, search = '', now = Date.now()): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ packageCode, search, savedAt: now } satisfies CheckoutIntent));
  } catch {
    // storage blocked (private mode): they will just land on the home page after sign-up
  }
}

export function readCheckoutIntent(now = Date.now()): CheckoutIntent | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const intent = JSON.parse(raw) as CheckoutIntent;
    if (typeof intent?.packageCode !== 'string' || !/^[A-Za-z0-9_-]{1,50}$/.test(intent.packageCode)
      || typeof intent.savedAt !== 'number' || now - intent.savedAt > CHECKOUT_INTENT_TTL_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return intent;
  } catch {
    return null;
  }
}

export function clearCheckoutIntent(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // nothing stored
  }
}

/** The /buy path an intent returns to. */
export function checkoutIntentPath(intent: CheckoutIntent): string {
  const search = intent.search && intent.search.startsWith('?') ? intent.search : '';
  return `/buy/${encodeURIComponent(intent.packageCode)}${search}`;
}
